import { Link, Navigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { Kicker } from "@/components/primitives";
import { ArrowRight, Check, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { BL_HERO_LOGO } from "@/pages/blackLeaf/assets";
import "@/mums/theme.css";
import "@/styles/blackLeaf.css";

export default function Welcome() {
  const { user } = useAuth();
  if (user && user !== null && user !== false) return <Navigate to="/home" replace />;

  return (
    <div className="min-h-screen px-5 py-10 sm:py-14" data-testid="welcome-page">
      {/* Hero */}
      <header className="max-w-2xl mx-auto h-card-double text-center px-6 sm:px-12 py-10 sm:py-14 animate-bloom-in" data-testid="welcome-card">
        <h1 className="font-display uppercase text-sage-dark leading-[0.95] tracking-[-0.01em]"
            style={{ fontSize: "clamp(2.4rem, 7vw, 3.8rem)", fontWeight: 700 }}>
          Welcome to<br />Bloom Keeper
        </h1>
        <div className="rule-label mt-6"><span className="font-display italic text-ink-soft tracking-[0.12em]">by <span className="not-italic font-semibold tracking-[0.2em] ml-1">HERITAGE DAHLIAS</span></span></div>
        <div className="mt-8 flex justify-center"><BrandLogo size={180} /></div>
        <p className="mt-8 font-display italic text-xl sm:text-2xl text-ink-soft">
          Your garden. Your records. Your Bloom Keeper.
        </p>
        <p className="mt-4 text-ink-soft max-w-lg mx-auto leading-relaxed">
          A free digital gardening companion to organise, record, and enjoy your growing journey.
          Track plants, seeds, tubers, cuttings, bloom times, garden beds, notes and more, all in one place.
        </p>
        <Link to="/auth" className="btn-heritage mt-8 inline-flex" data-testid="welcome-enter-btn">
          Create my Bloom Keeper <ArrowRight size={16} />
        </Link>
      </header>

      {/* What it IS / IS NOT */}
      <section className="max-w-2xl mx-auto mt-10 grid sm:grid-cols-2 gap-4" data-testid="welcome-is-grid">
        <div className="h-card p-6">
          <Kicker>What Bloom Keeper IS</Kicker>
          <ul className="mt-4 space-y-2 text-ink">
            {["A personal garden journal","Record plants, tubers, seeds, cuttings","Track growth stages and bloom times","Organise garden beds and locations","A growing record of your journey"].map((t) => (
              <li key={t} className="flex gap-2"><Check size={16} className="text-sage-dark mt-1 shrink-0" /><span>{t}</span></li>
            ))}
          </ul>
        </div>
        <div className="h-card p-6">
          <Kicker>What it IS NOT</Kicker>
          <ul className="mt-4 space-y-2 text-ink">
            {["A subscription service","A paid membership","A marketing funnel","A place your information is sold","A source of spam emails"].map((t) => (
              <li key={t} className="flex gap-2"><X size={16} className="text-terracotta mt-1 shrink-0" /><span>{t}</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* Privacy */}
      <section className="max-w-2xl mx-auto mt-6 h-card p-6 sm:p-8" data-testid="welcome-privacy">
        <Kicker>Privacy & transparency</Kicker>
        <p className="mt-3 font-display italic text-ink-soft text-lg">Your privacy matters.</p>
        <p className="mt-2 text-ink leading-relaxed">
          We only ask for your name and email so your Bloom Keeper can be associated with you. No hidden fees, no subscriptions, no unwanted marketing, no complicated setup. Simply create your profile and begin growing.
        </p>
        <div className="mt-5 border-t border-heritage-border pt-4">
          <Kicker>Why a profile?</Kicker>
          <p className="mt-2 text-ink leading-relaxed">
            Think of it like writing your name on the cover of a gardening journal. Your profile keeps your records uniquely yours.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-2xl mx-auto mt-8 text-center">
        <p className="font-display italic text-xl text-ink-soft">Ready to start your gardening journey?</p>
        <Link to="/auth" className="btn-heritage mt-4 inline-flex" data-testid="welcome-enter-btn-bottom">
          Create my Bloom Keeper <ArrowRight size={16} />
        </Link>
        <p className="mt-6 text-sm text-ink-soft">
          Already signed up? <Link to="/auth" className="underline text-sage-dark">Sign in</Link>.
        </p>
      </section>

      <footer className="max-w-2xl mx-auto mt-12 text-center text-sm text-ink-soft">
        <p>Tended by hand · Mangaroa Valley · since 1932.</p>
        <p className="mt-2">
          <Link to="/terms" className="underline">Terms of Use</Link>
          <span className="mx-2">·</span>
          <a href="mailto:Heritagedahliasnz@gmail.com" className="underline">Contact</a>
        </p>
      </footer>

      {/* Portal pill bar — fixed bottom row of serif pills */}
      <nav className="portal-pill-bar" data-testid="portal-pill-bar" aria-label="Private portals">
        <Link
          to="/mums-shed/login"
          className="portal-pill pill-blue"
          data-testid="mums-portal-link"
          aria-label="Mum's Shed"
        >
          Mum&rsquo;s Shed
        </Link>
        <Link
          to="/kaylas-garden/login"
          className="portal-pill pill-green"
          data-testid="kaylas-portal-link"
          aria-label="Te Whare Iti Kākāriki"
        >
          Te Whare Iti Kākāriki
          <span className="pill-sub">The Little Green House</span>
        </Link>
        <Link
          to="/black-leaf-society"
          className="portal-pill pill-plum-logo"
          data-testid="black-leaf-portal-link"
          aria-label="The Black Leaf Dahlia Society"
        >
          <img src={BL_HERO_LOGO} alt="" className="portal-pill-seal" />
          <span className="portal-pill-text">Black Leaf Dahlia Society</span>
        </Link>
      </nav>
    </div>
  );
}
